import { useRef, useState, useCallback } from 'react';

import type React from 'react';

export interface IDraggableTabsOptions {
  disabled?: boolean;
  onReorder?: (from: number, to: number) => void;
}

export const useDraggableTabs = (options: IDraggableTabsOptions) => {
  const { disabled, onReorder } = options;

  const dragFromIndex = useRef<number | null>(null);
  const dragToIndex = useRef<number | null>(null);

  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const onDragStart = useCallback(
    (index: number) => {
      if (disabled) return;
      dragFromIndex.current = index;
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (index: number, e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      dragToIndex.current = index;
      setDragOverIndex(index);
    },
    [disabled],
  );

  const onDrop = useCallback(() => {
    if (
      dragFromIndex.current === null ||
      dragToIndex.current === null ||
      dragFromIndex.current === dragToIndex.current
    ) {
      reset();
      return;
    }

    onReorder?.(dragFromIndex.current, dragToIndex.current);
    reset();
  }, [onReorder]);

  const reset = () => {
    dragFromIndex.current = null;
    dragToIndex.current = null;
    setDragOverIndex(null);
  };

  const onDragEnd = useCallback(() => {
    reset();
  }, []);

  return {
    dragOverIndex,
    draggableProps: (index: number) => ({
      draggable: !disabled,
      onDragStart: () => onDragStart(index),
      onDragOver: (e: React.DragEvent) => onDragOver(index, e),
      onDrop,
      onDragEnd,
    }),
  };
};
